import * as Yup from "yup";
import i18next from "../../../i18n";
import { DeliveryTime } from "../../../types/deliveries.type";

/**
 * The create-delivery form values (`NewDeliveryForm`, Deliveries flow Task 6), moved out of the
 * component during the Carbon migration so the schema and the field shape live together and can be
 * unit-tested without rendering — the same extraction `newSiteValidationSchema.ts` established.
 *
 * Identical field set to the pre-Carbon `FormValues` type it replaces: nothing added, nothing
 * dropped, so the object handed to `useCreateDelivery` stays byte-equivalent (see
 * `buildDeliveryPayload`).
 */
export type NewDeliveryFormValues = {
  orderName: string;
  purchaseOrderNumber: string;
  supplierEmails: string[];
  receiverEmail?: string;
  deliveryDate: string;
  siteId: string;
  deliveryTime: DeliveryTime;
  purchaseOrderFile: string;
  comment?: string;
};

export const allowedExtensions = [
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "txt",
  "rtf",
];

/**
 * Used when a specific awarded supplier is selected (`selectedSupplier`): `supplierEmails` is
 * required (the supplier's own email is auto-filled). Copied verbatim from the pre-Carbon
 * `utils/validations.ts` so every message key — and therefore every e2e-asserted error string —
 * is unchanged; `utils/validations.ts` now re-exports both schemas from here to keep its single
 * import path (and the existing `deliveryValidations.test.ts`) working.
 */
export const deliveryValidationSchema = Yup.object().shape({
  orderName: Yup.string().required(
    i18next.t("deliveries.form.validations.orderName")
  ),
  purchaseOrderNumber: Yup.string().required(
    i18next.t("deliveries.form.validations.purchaseOrderNumber")
  ),
  supplierEmails: Yup.array()
    .of(Yup.string().email(i18next.t("validations.validEmail")))
    .min(1, i18next.t("deliveries.form.validations.emailsRequired"))
    .required(i18next.t("deliveries.form.validations.emailsRequired")),
  receiverEmail: Yup.string().email(i18next.t("validations.validEmail")),
  deliveryDate: Yup.string().required(
    i18next.t("deliveries.form.validations.deliveryDate")
  ),
  deliveryTime: Yup.string().required(
    i18next.t("deliveries.form.validations.deliveryTime")
  ),
  siteId: Yup.string().required(i18next.t("validations.selectDeliveryPlace")),
  purchaseOrderFile: Yup.string(),
  comment: Yup.string().max(255, i18next.t("validations.comment255")),
});

/**
 * Used for a manual (no pre-selected supplier) delivery: `supplierEmails` is optional. This is the
 * schema the `/deliveries/new` e2e gating test exercises — its four asserted messages
 * (`orderName`, `purchaseOrderNumber`, `deliveryDate`, `selectDeliveryPlace`) come from here.
 */
export const manualDeliveryValidationSchema = Yup.object().shape({
  orderName: Yup.string().required(
    i18next.t("deliveries.form.validations.orderName")
  ),
  purchaseOrderNumber: Yup.string().required(
    i18next.t("deliveries.form.validations.purchaseOrderNumber")
  ),
  supplierEmails: Yup.array().of(
    Yup.string().email(i18next.t("validations.validEmail"))
  ),
  receiverEmail: Yup.string().email(i18next.t("validations.validEmail")),
  deliveryDate: Yup.string().required(
    i18next.t("deliveries.form.validations.deliveryDate")
  ),
  deliveryTime: Yup.string().required(
    i18next.t("deliveries.form.validations.deliveryTime")
  ),
  siteId: Yup.string().required(i18next.t("validations.selectDeliveryPlace")),
  purchaseOrderFile: Yup.string(),
  comment: Yup.string().max(255, i18next.t("validations.comment255")),
});
